import {
  getSignPdfPageScope,
  hasSignPdfSignature,
  hasSignPdfSigningIntent,
  SIGN_PDF_SCOPES,
} from "./signPdfSettings";
import { getSignPdfTemplate, SIGN_PDF_FIELD_KINDS } from "./signPdfPlacements";

/** Guided signing phases shown in the Sign toolbar. */
export const SIGN_PDF_WORKFLOW_STEPS = {
  UPLOAD: "upload",
  WHO: "who",
  SETUP: "setup",
  FORMAT: "format",
  PLACE: "place",
  REVIEW: "review",
  FINISH: "finish",
};

export const SIGN_PDF_WORKFLOW_STEP_LABELS = {
  [SIGN_PDF_WORKFLOW_STEPS.UPLOAD]: "Upload document",
  [SIGN_PDF_WORKFLOW_STEPS.WHO]: "Who is signing",
  [SIGN_PDF_WORKFLOW_STEPS.SETUP]: "Your signature",
  [SIGN_PDF_WORKFLOW_STEPS.FORMAT]: "Signing format",
  [SIGN_PDF_WORKFLOW_STEPS.PLACE]: "Place fields",
  [SIGN_PDF_WORKFLOW_STEPS.REVIEW]: "Review",
  [SIGN_PDF_WORKFLOW_STEPS.FINISH]: "Finish",
};

/** Setup checklist items for the Fields dropdown. */
export function getSignPdfSetupChecklist(session) {
  const signature = getSignPdfTemplate(session, SIGN_PDF_FIELD_KINDS.SIGNATURE);
  const initials = getSignPdfTemplate(session, SIGN_PDF_FIELD_KINDS.INITIALS);
  const hasSignature = hasSignPdfSignature(signature);
  const hasInitials = hasSignPdfSignature(initials);
  const hasIntent = hasSignPdfSigningIntent(session);

  const required = [
    {
      id: "signature",
      label: "Signature",
      required: true,
      done: hasSignature,
    },
  ];
  const optional = [
    {
      id: "initials",
      label: "Initials",
      required: false,
      done: hasInitials,
    },
  ];
  const format = {
    id: "format",
    label: "Signing format",
    required: true,
    done: hasIntent,
  };

  const doneCount =
    (hasSignature ? 1 : 0) + (hasIntent ? 1 : 0);
  const totalRequired = 2;

  return {
    required,
    optional,
    format,
    doneCount,
    totalRequired,
    complete: hasSignature && hasIntent,
    signatureReady: hasSignature,
  };
}

export function deriveSignPdfWorkflowStep(
  session,
  { explicitStep = null, inReview = false, hasDocument = false } = {}
) {
  if (explicitStep === SIGN_PDF_WORKFLOW_STEPS.FINISH) {
    return SIGN_PDF_WORKFLOW_STEPS.FINISH;
  }
  if (inReview || explicitStep === SIGN_PDF_WORKFLOW_STEPS.REVIEW) {
    return SIGN_PDF_WORKFLOW_STEPS.REVIEW;
  }

  if (!hasDocument) {
    return SIGN_PDF_WORKFLOW_STEPS.UPLOAD;
  }
  if (!session?.signPdfWhoChosen) {
    return SIGN_PDF_WORKFLOW_STEPS.WHO;
  }
  if (!hasSignPdfSigningIntent(session)) {
    return SIGN_PDF_WORKFLOW_STEPS.FORMAT;
  }
  const setup = getSignPdfSetupChecklist(session);
  if (!setup.signatureReady) {
    return SIGN_PDF_WORKFLOW_STEPS.SETUP;
  }
  return SIGN_PDF_WORKFLOW_STEPS.PLACE;
}

export function isSignPdfBlocksUnlocked(session) {
  return getSignPdfSetupChecklist(session).signatureReady;
}

export function countSignPdfPagesDone(pageDoneMap = {}, pageCount = 0) {
  let n = 0;
  for (let p = 1; p <= pageCount; p += 1) {
    if (pageDoneMap[String(p)]) n += 1;
  }
  return n;
}

export function allSignPdfPagesDone(pageDoneMap = {}, pageCount = 0) {
  if (pageCount <= 0) return false;
  return countSignPdfPagesDone(pageDoneMap, pageCount) >= pageCount;
}

export function shouldShowPerPageDone(session) {
  return getSignPdfPageScope(session) === SIGN_PDF_SCOPES.PER_PAGE;
}

export function signPdfWorkflowHint(step, session, { currentPage = 1 } = {}) {
  const scope = getSignPdfPageScope(session);
  switch (step) {
    case SIGN_PDF_WORKFLOW_STEPS.UPLOAD:
      return "Upload a PDF to get started.";
    case SIGN_PDF_WORKFLOW_STEPS.WHO:
      return "Choose who is signing to continue.";
    case SIGN_PDF_WORKFLOW_STEPS.SETUP:
      return "Create your signature to get started.";
    case SIGN_PDF_WORKFLOW_STEPS.FORMAT:
      return "Choose how your signature applies across pages.";
    case SIGN_PDF_WORKFLOW_STEPS.PLACE:
      if (scope === SIGN_PDF_SCOPES.PER_PAGE) {
        return `Place fields on page ${currentPage}, then mark Done.`;
      }
      if (scope === SIGN_PDF_SCOPES.CURRENT) {
        return "Place your fields on this page.";
      }
      return "Place fields once — they apply to every page.";
    case SIGN_PDF_WORKFLOW_STEPS.REVIEW:
      return "Check each page, then finish to download.";
    case SIGN_PDF_WORKFLOW_STEPS.FINISH:
      return "Download, print, or share your signed PDF.";
    default:
      return "";
  }
}
